import { DefaultTheme } from 'styled-components';

/**
 * Safely get a value from the theme with a fallback
 * 
 * This function safely accesses a property from the theme using a selector function.
 * If the theme is undefined or the selector throws an error, it returns the fallback value.
 * 
 * @param theme - The styled-components theme object
 * @param selector - A function that selects a value from the theme
 * @param fallback - The fallback value to use if the theme or selector fails
 * @returns The selected theme value or the fallback
 */
export const getThemeValue = <T>(
  theme: DefaultTheme | undefined,
  selector: (theme: DefaultTheme) => T,
  fallback: T
): T => {
  try {
    if (!theme) return fallback;
    return selector(theme);
  } catch (error) {
    console.warn('Error accessing theme property:', error);
    return fallback;
  }
};

/**
 * Create a theme-aware styled component function with safety
 * 
 * This function creates a styled-component template literal function that safely
 * accesses the theme. If the theme is undefined or the function throws an error,
 * it returns an empty string to prevent breaking styles.
 * 
 * @param styleFunction - A function that takes a theme and returns a CSS string
 * @returns A function that can be used in styled-components template literals
 */
export const themeAware = (styleFunction: (theme: DefaultTheme) => string) => {
  return (props: { theme?: DefaultTheme }) => {
    try {
      if (!props.theme) {
        console.warn('Theme is undefined in themeAware function');
        // Return empty string to prevent breaking styles
        return '';
      }
      return styleFunction(props.theme);
    } catch (error) {
      console.warn('Error in themeAware:', error);
      // Return empty string to prevent breaking styles
      return '';
    }
  };
};

/**
 * Get a color from the theme with a fallback
 * 
 * This is a specialized version of getThemeValue for colors.
 * 
 * @param theme - The styled-components theme object
 * @param path - The path to the color in the theme (e.g., 'primary.500')
 * @param fallback - The fallback color to use if the theme or path fails
 * @returns The selected color or the fallback
 */
export const getThemeColor = (
  theme: DefaultTheme | undefined,
  path: string,
  fallback: string = '#000000'
): string => {
  try {
    if (!theme || !theme.colors) return fallback;
    
    // Split the path into parts (e.g., 'primary.500' -> ['primary', '500'])
    const parts = path.split('.');
    
    // Start with the colors object
    let value: any = theme.colors;
    
    // Traverse the path
    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return fallback;
      }
    }
    
    // If the final value is a string, return it, otherwise return the fallback
    return typeof value === 'string' ? value : fallback;
  } catch (error) {
    console.warn(`Error getting theme color at path "${path}":`, error);
    return fallback;
  }
};

/**
 * Get a spacing value from the theme with a fallback
 * 
 * This is a specialized version of getThemeValue for spacing.
 * 
 * @param theme - The styled-components theme object
 * @param key - The key of the spacing value in the theme
 * @param fallback - The fallback spacing to use if the theme or key fails
 * @returns The selected spacing value or the fallback
 */
export const getThemeSpacing = (
  theme: DefaultTheme | undefined,
  key: string | number,
  fallback: string = '8px'
): string => {
  try {
    if (!theme || !theme.spacing) return fallback;
    
    // If the key is a number, convert it to a string
    const spacingKey = typeof key === 'number' ? key.toString() : key;
    
    // Check if the key exists in the spacing object
    if (spacingKey in theme.spacing) {
      const value = theme.spacing[spacingKey as keyof typeof theme.spacing];
      return typeof value === 'string' ? value : `${value}px`;
    }
    
    return fallback;
  } catch (error) {
    console.warn(`Error getting theme spacing for key "${key}":`, error);
    return fallback;
  }
};

/**
 * Default theme values to use as fallbacks
 */
export const defaultThemeValues = {
  colors: {
    primary: {
      '50': '#e3f2fd',
      '100': '#bbdefb',
      '200': '#90caf9',
      '300': '#64b5f6',
      '400': '#42a5f5',
      '500': '#2196f3',
      '600': '#1e88e5',
      '700': '#1976d2',
      '800': '#1565c0',
      '900': '#0d47a1'
    },
    secondary: {
      '50': '#fce4ec',
      '100': '#f8bbd0',
      '200': '#f48fb1',
      '300': '#f06292',
      '400': '#ec407a',
      '500': '#e91e63',
      '600': '#d81b60',
      '700': '#c2185b',
      '800': '#ad1457',
      '900': '#880e4f'
    },
    neutral: {
      '50': '#fafafa',
      '100': '#f5f5f5',
      '200': '#eeeeee',
      '300': '#e0e0e0',
      '400': '#bdbdbd',
      '500': '#9e9e9e',
      '600': '#757575',
      '700': '#616161',
      '800': '#424242',
      '900': '#212121'
    },
    semantic: {
      error: {
        light: '#ffebee',
        default: '#f44336',
        dark: '#c62828'
      },
      warning: {
        light: '#fff8e1',
        default: '#ffc107',
        dark: '#ff8f00'
      },
      success: {
        light: '#e8f5e9',
        default: '#4caf50',
        dark: '#2e7d32'
      },
      info: {
        light: '#e3f2fd',
        default: '#2196f3',
        dark: '#0d47a1'
      }
    },
    theme: {
      background: {
        primary: '#ffffff',
        secondary: '#f5f5f5'
      },
      text: {
        primary: '#212121',
        secondary: '#757575'
      },
      border: {
        light: '#e0e0e0',
        medium: '#bdbdbd',
        dark: '#9e9e9e'
      }
    }
  },
  spacing: {
    '0': '0',
    '1': '1px',
    '2': '2px',
    '4': '4px',
    '8': '8px',
    '12': '12px',
    '16': '16px',
    '24': '24px',
    '32': '32px',
    '40': '40px',
    '48': '48px',
    '64': '64px',
    '80': '80px',
    '96': '96px',
    '128': '128px'
  }
};

export default {
  getThemeValue,
  themeAware,
  getThemeColor,
  getThemeSpacing,
  defaultThemeValues
};